import AddCommentIcon from '@mui/icons-material/AddComment'
import CommentIcon from '@mui/icons-material/Comment'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useState } from 'react'

import type { CommentSectionProps } from './CommentSection.types'

import { useCommentsQuery, useCreateCommentMutation } from '@/shared/api/comments.queries'

export const CommentSection: React.FC<CommentSectionProps> = ({ objectUuid }) => {
  const theme = useTheme()
  const [commentText, setCommentText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: comments, isLoading, refetch } = useCommentsQuery(objectUuid)
  const createCommentMutation = useCreateCommentMutation()

  const handleSubmit = async () => {
    if (!commentText.trim()) {
      setError('Comment text is required')
      return
    }

    try {
      setError(null)
      await createCommentMutation.mutateAsync({
        objectUuid,
        commentText: commentText.trim(),
      })
      setCommentText('')
      await refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Stack spacing={3}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 4,
            height: 24,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
          }}
        />
        Comments
      </Typography>

      <Box sx={{ pl: 2 }}>
        <Stack spacing={3}>
          {/* Add Comment Section */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                mb: 2,
              }}
            >
              Add New Comment
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Enter your comment..."
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              error={!!error}
              helperText={error}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
              }}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddCommentIcon />}
                onClick={handleSubmit}
                disabled={createCommentMutation.isPending || !commentText.trim()}
                size="small"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                }}
              >
                {createCommentMutation.isPending ? 'Adding...' : 'Add Comment'}
              </Button>
            </Box>
          </Box>

          {/* Comments List Section */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                mb: 2,
              }}
            >
              Comments History
            </Typography>

            {isLoading && (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!isLoading && comments && comments.length > 0 && (
              <Stack spacing={2}>
                {comments.map(comment => (
                  <Card
                    key={comment.uuid}
                    variant="outlined"
                    sx={{
                      borderColor: alpha(theme.palette.divider, 0.2),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(comment.date)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {comment.commentText}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}

            {!isLoading && (!comments || comments.length === 0) && (
              <Box
                sx={{
                  py: 3,
                  px: 2,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.background.default, 0.3),
                  borderRadius: 1,
                }}
              >
                <CommentIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No comments yet. Be the first to add a comment.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}
